import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  const filteredComics = useMemo(() => {
    let result = slug === 'all' ? [...comics] : comics.filter((c) => c.category === slug);

    switch (sortBy) {
      case 'popular':
        result.sort((a, b) => {
          const aViews = parseFloat(a.views.replace('万', '')) * 10000;
          const bViews = parseFloat(b.views.replace('万', '')) * 10000;
          return bViews - aViews;
        });
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'latest':
      default:
        result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        break;
    }
    return result;
  }, [slug, sortBy]);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 韩漫星球`,
    description: `韩漫星球${currentCategory.name}漫画分类，海量正版韩国漫画在线观看。`,
    url: `https://manhwaplanet.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫星球',
      url: 'https://manhwaplanet.com',
    },
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory.name}漫画 - 韩漫星球`}
        description={`韩漫星球${currentCategory.name}漫画分类页面，海量正版韩国漫画在线观看。下拉式韩漫阅读，畅享${currentCategory.name}类型精彩内容。`}
        keywords={`韩漫网站,韩国漫画,${currentCategory.name}漫画,下拉式韩漫,正版韩漫`}
        canonicalUrl={`https://manhwaplanet.com/category/${slug}`}
        schema={categorySchema}
      />

      <Breadcrumb />

      <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        {/* Category Tabs */}
        <section className="mb-8">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
            {currentCategory.name}漫画
          </h1>
          <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </section>

        {/* Sort + Count */}
        <section className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <p className="text-sm text-foreground-500">
            共找到 <strong className="text-foreground-900">{filteredComics.length}</strong> 部漫画
          </p>
          <div className="flex items-center gap-2 bg-background-100 rounded-full p-1">
            {[
              { key: 'latest' as const, label: '最新' },
              { key: 'popular' as const, label: '人气' },
              { key: 'rating' as const, label: '评分' },
            ].map((s) => (
              <button
                key={s.key}
                onClick={() => setSortBy(s.key)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  sortBy === s.key
                    ? 'bg-background-50 text-foreground-900'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </section>

        {/* Comic Grid */}
        <section>
          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} variant="default" />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无漫画</p>
              <Link to="/category/all" className="inline-block mt-4 text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
                查看全部漫画
              </Link>
            </div>
          )}
        </section>
      </main>
    </>
  );
}